import { IRequest } from './types';

/** Translation key for a request whose origin service was never determined. */
export const REQUESTER_NOT_RECORDED = 'hospital.departments.requester_not_recorded';

/**
 * SHER-910 — how the origin service of a request reads in the request tables.
 *
 * Renders the snapshot the API took when the request was created, in the shape Pedro asked for on
 * 2026-09-16: "Neurologie — Unité AVC" when the unit is known, "Neurologie" when only the
 * department is, "Non renseigné" when neither is.
 *
 * All three are normal. The API writes only what the requesting doctor's unit memberships can
 * prove (sherwood-api/src/utils/requesterService.ts), so a multi-unit doctor legitimately yields
 * a department with no unit, and a doctor whose units span departments yields nothing at all.
 * Every request created before SHER-910 shipped also yields nothing, permanently: no audit of
 * researcher-to-unit membership exists, so their origin is unknowable rather than merely missing.
 *
 * The one thing this must never do is fall back to the doctor's CURRENT department. That was the
 * old behaviour and it is what the ticket removes: it rewrote the origin of every past request
 * each time someone changed team, and it was indistinguishable from a recorded fact.
 *
 * Returns null rather than a translated sentinel, so "we do not know" is a typed absence and this
 * module returns only data — the same split as its sibling requestTableColumns.ts, which hands
 * back labelIds and leaves resolution to the caller. The caller pairs it with
 * REQUESTER_NOT_RECORDED.
 */
export function requesterServiceName(request: IRequest): string | null {
    const department = request?.requesterDepartment?.name;
    if (!department) {
        // A unit without a department is not shown on its own: it would be an unlabelled fragment
        // in a column whose heading promises a service.
        return null;
    }

    const unit = request?.requesterUnit?.name;
    return unit ? `${department} — ${unit}` : department;
}
